import { useCallback } from 'react';

import { useNavigate } from '@tanstack/react-router';

import { resolvePath } from '@/lib/formatters/resolveEnvUrl';
import { ANGULAR_HOME_PATH_BY_USER_TYPE, REACT_HOME_ROUTE, REACT_HOME_USER_TYPES } from '@/lib/validators/userRoles';
import { useUser } from '@/store/useUser';

/**
 * Returns the home route string for the current user.
 *
 * - Managers (COORDENADOR_ESCOLA/TURMA/SAAS) → '/home/coordenador' (internal React path)
 * - All other types → full Angular URL via resolvePath (mirrors HubsService)
 *
 * Use this for declarative links (`<Link to={useHomeRoute()}>`) and
 * pathname comparisons. For imperative navigation (button onClick, onBack),
 * use `useGoHome()` instead.
 */
export function useHomeRoute(): string {
  const { user } = useUser();

  if (user === null) return REACT_HOME_ROUTE;

  if (REACT_HOME_USER_TYPES.has(user.perfil)) {
    return REACT_HOME_ROUTE;
  }

  const angularPath = ANGULAR_HOME_PATH_BY_USER_TYPE[user.perfil];
  return angularPath !== undefined ? resolvePath(angularPath) : REACT_HOME_ROUTE;
}

/**
 * Returns a callback that navigates the current user to their home page.
 * Handles both internal React routes and external Angular redirects.
 *
 * Use this for button onClick handlers and PageHeader onBack props.
 * For declarative links, use `useHomeRoute()` instead.
 */
export function useGoHome(): () => void {
  const homeRoute = useHomeRoute();
  const navigate = useNavigate();

  return useCallback(() => {
    if (homeRoute.startsWith('http://') || homeRoute.startsWith('https://')) {
      window.location.href = homeRoute;
    } else {
      void navigate({ to: homeRoute });
    }
  }, [homeRoute, navigate]);
}
